import { COGNITO } from '@/config/constants'
import type { IAuthService } from './types'
import { MockAuthService } from './mock-auth-service'
import { CognitoAuthService } from './cognito-auth-service'

/**
 * Fail closed: a deployed build must never fall through to MockAuthService.
 *
 * MockAuthService accepts ANY credentials, which is the correct local-dev
 * behaviour and a full authentication bypass anywhere else. `COGNITO.userPoolId`
 * is resolved at RUNTIME from `window.__EDGE_RUNTIME_CONFIG__` (the web app
 * ships an empty stub of `/runtime-config.js`) with a Vite-env fallback, so a
 * missing, stubbed, or ad-blocked runtime-config file is enough to leave it
 * empty in production. Without this guard that misconfiguration produces a
 * normal-looking login form on a wide-open app, with no error anywhere.
 *
 * Breaking loudly is the lesser failure. Do not soften this to a warning.
 */
if (import.meta.env.PROD && !COGNITO.userPoolId) {
  throw new Error(
    'Auth misconfigured: production build has no Cognito user pool. ' +
      'Refusing to start rather than fall back to MockAuthService, which accepts any credentials. ' +
      'Check VITE_COGNITO_USER_POOL_ID at build time and __EDGE_RUNTIME_CONFIG__ / runtime-config.js at runtime.',
  )
}

/**
 * Singleton auth service instance.
 * Uses CognitoAuthService when Cognito is configured, MockAuthService otherwise.
 * The MockAuthService branch is reachable only in non-production builds -- see
 * the fail-closed guard above.
 *
 * Internal use only — feature code reads the session from useAccountStore and
 * drives auth through the store actions. ESLint blocks token/refresh calls on
 * this object outside services/auth.
 */
export const authService: IAuthService = COGNITO.userPoolId
  ? new CognitoAuthService()
  : new MockAuthService()

export type {
  IAuthService,
  AuthSession,
  AuthTokens,
  AuthUser,
  RegisterData,
  PasswordResetData,
} from './types'
